import { useState, useRef, useEffect } from 'react';
import { apiClient, AdminAPIKey, AdminUser, ModelInfo, usePlatformStore } from '@edgerunner/shared';
import { useAuthStore } from '../stores/authStore';
import { ProductFeedbackModal } from './ProductFeedbackModal';

export function ProfileMenu() {
  const deploymentMode = usePlatformStore((s) => s.deploymentMode);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const [isOpen, setIsOpen] = useState(false);
  const [showAdminModels, setShowAdminModels] = useState(false);
  const [showAdminUsers, setShowAdminUsers] = useState(false);
  const [showAdminAPIKeys, setShowAdminAPIKeys] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [apiKeys, setAPIKeys] = useState<AdminAPIKey[]>([]);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserAdmin, setNewUserAdmin] = useState(false);
  const [newUserPiiPhiEligible, setNewUserPiiPhiEligible] = useState(false);
  const [newAPIKeyName, setNewAPIKeyName] = useState('');
  const [newAPIKeyUserId, setNewAPIKeyUserId] = useState('');
  const [createdAPIKey, setCreatedAPIKey] = useState<string | null>(null);
  const [apiKeyCopied, setAPIKeyCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Don't render in desktop deployment mode (no auth, no profile)
  if (deploymentMode === 'desktop') return null;

  // Don't render anything if not authenticated
  if (!isAuthenticated || !user) return null;

  const displayName =
    user.first_name || user.last_name
      ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
      : null;

  const initials = displayName
    ? displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase();

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  const openAdminModels = async () => {
    setIsOpen(false);
    setShowAdminModels(true);
    setAdminLoading(true);
    setAdminError(null);
    try {
      setModels(await apiClient.models.getModels());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to load models');
    } finally {
      setAdminLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    setAdminLoading(true);
    setAdminError(null);
    try {
      setUsers(await apiClient.admin.listUsers());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to load users');
    } finally {
      setAdminLoading(false);
    }
  };

  const openAdminUsers = async () => {
    setIsOpen(false);
    setShowAdminUsers(true);
    await loadAdminUsers();
  };

  const loadAdminAPIKeys = async () => {
    setAdminLoading(true);
    setAdminError(null);
    try {
      const [loadedKeys, loadedUsers] = await Promise.all([
        apiClient.admin.listAPIKeys(),
        apiClient.admin.listUsers(),
      ]);
      setAPIKeys(loadedKeys);
      setUsers(loadedUsers);
      setNewAPIKeyUserId((current) => current || user.id || loadedUsers[0]?.id || '');
    } catch (err: any) {
      setAdminError(err.message || 'Failed to load API keys');
    } finally {
      setAdminLoading(false);
    }
  };

  const openAdminAPIKeys = async () => {
    setIsOpen(false);
    setShowAdminAPIKeys(true);
    setCreatedAPIKey(null);
    setAPIKeyCopied(false);
    await loadAdminAPIKeys();
  };

  const createAPIKey = async () => {
    if (!newAPIKeyName.trim() || !newAPIKeyUserId) {
      setAdminError('A key name and user are required.');
      return;
    }
    setAdminLoading(true);
    setAdminError(null);
    try {
      const result = await apiClient.admin.createAPIKey({
        name: newAPIKeyName.trim(),
        user_id: newAPIKeyUserId,
      });
      setCreatedAPIKey(result.key);
      setAPIKeyCopied(false);
      setNewAPIKeyName('');
      setAPIKeys(await apiClient.admin.listAPIKeys());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to create API key');
    } finally {
      setAdminLoading(false);
    }
  };

  const copyCreatedAPIKey = async () => {
    if (!createdAPIKey) return;
    try {
      await navigator.clipboard.writeText(createdAPIKey);
      setAPIKeyCopied(true);
    } catch {
      setAdminError('Could not copy automatically. Select and copy the key manually.');
    }
  };

  const revokeAPIKey = async (key: AdminAPIKey) => {
    if (!window.confirm(`Revoke API key "${key.name}" for ${key.user_email}?`)) return;
    setAdminLoading(true);
    setAdminError(null);
    try {
      await apiClient.admin.revokeAPIKey(key.id);
      setAPIKeys(await apiClient.admin.listAPIKeys());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to revoke API key');
    } finally {
      setAdminLoading(false);
    }
  };

  const createUser = async () => {
    if (!newUserEmail.trim() || !newUserPassword) {
      setAdminError('Email and password are required.');
      return;
    }
    setAdminLoading(true);
    setAdminError(null);
    try {
      await apiClient.admin.createUser({
        email: newUserEmail.trim(),
        password: newUserPassword,
        is_superuser: newUserAdmin,
        pii_phi_eligible: newUserPiiPhiEligible,
        is_active: true,
        is_verified: true,
      });
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserAdmin(false);
      setNewUserPiiPhiEligible(false);
      setUsers(await apiClient.admin.listUsers());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to create user');
    } finally {
      setAdminLoading(false);
    }
  };

  const updateUser = async (target: AdminUser, patch: Partial<AdminUser>) => {
    setAdminLoading(true);
    setAdminError(null);
    try {
      await apiClient.admin.updateUser(target.id, {
        is_active: patch.is_active,
        is_superuser: patch.is_superuser,
        is_verified: patch.is_verified,
        pii_phi_eligible: patch.pii_phi_eligible,
      });
      setUsers(await apiClient.admin.listUsers());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to update user');
    } finally {
      setAdminLoading(false);
    }
  };

  const resetPassword = async (target: AdminUser) => {
    const password = window.prompt(`Set a new password for ${target.email}. Minimum 8 characters.`);
    if (!password) return;
    if (password.length < 8) {
      setAdminError('Password must be at least 8 characters.');
      return;
    }
    setAdminLoading(true);
    setAdminError(null);
    try {
      await apiClient.admin.resetPassword(target.id, password);
      setUsers(await apiClient.admin.listUsers());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to reset password');
    } finally {
      setAdminLoading(false);
    }
  };

  const deleteUser = async (target: AdminUser) => {
    const counts = target.resource_counts;
    const confirmed = window.confirm(
      `Delete user "${target.email}"?\n\nThis will purge ${counts.chat_histories} chats, ${counts.document_files} documents, and ${counts.collections} collections.`
    );
    if (!confirmed) return;
    setAdminLoading(true);
    setAdminError(null);
    try {
      await apiClient.admin.deleteUser(target.id);
      setUsers(await apiClient.admin.listUsers());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to delete user');
    } finally {
      setAdminLoading(false);
    }
  };

  const deleteModel = async (modelId: string) => {
    const confirmed = window.confirm(`Delete model "${modelId}"? This cannot be undone.`);
    if (!confirmed) return;
    setAdminLoading(true);
    setAdminError(null);
    try {
      const result = await apiClient.models.deleteModelById(modelId, true);
      if (!result.deleted) {
        throw new Error('Model deletion failed');
      }
      setModels(await apiClient.models.getModels());
    } catch (err: any) {
      setAdminError(err.message || 'Failed to delete model');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 rounded-full bg-red-700 hover:bg-red-600 text-neutral-100 text-xs font-semibold flex items-center justify-center cursor-pointer transition-colors duration-200 border-0"
        title="Manage user"
        aria-label="Manage user"
      >
        {initials}
      </button>

      {/* Popover */}
      {isOpen && (
        <div className="absolute bottom-full right-0 md:right-auto md:left-0 mb-2 w-60 bg-neutral-700 border border-neutral-400 rounded-medium shadow-lg z-[3000]">
          {/* User info */}
          <div className="px-sm py-sm border-b border-neutral-400">
            {displayName && (
              <p className="text-sm font-semibold text-neutral-100 truncate">
                {displayName}
              </p>
            )}
            <p className="text-xs text-neutral-300 truncate">{user.email}</p>
          </div>

          {/* Actions */}
          <div className="p-xs">
            {user.is_superuser && (
              <>
                <button
                  onClick={openAdminUsers}
                  className="w-full text-left px-sm py-xs text-sm text-neutral-200 hover:bg-neutral-500 rounded-small cursor-pointer transition-colors duration-150 flex items-center gap-xs bg-transparent border-0"
                >
                  <i className="fas fa-users-cog text-xs" />
                  Admin Users
                </button>
                <button
                  onClick={openAdminModels}
                  className="w-full text-left px-sm py-xs text-sm text-neutral-200 hover:bg-neutral-500 rounded-small cursor-pointer transition-colors duration-150 flex items-center gap-xs bg-transparent border-0"
                >
                  <i className="fas fa-shield-alt text-xs" />
                  Admin Models
                </button>
                <button
                  onClick={openAdminAPIKeys}
                  className="w-full text-left px-sm py-xs text-sm text-neutral-200 hover:bg-neutral-500 rounded-small cursor-pointer transition-colors duration-150 flex items-center gap-xs bg-transparent border-0"
                >
                  <i className="fas fa-key text-xs" />
                  Admin API Keys
                </button>
              </>
            )}
            <button
              onClick={() => {
                setIsOpen(false);
                setShowFeedback(true);
              }}
              className="w-full text-left px-sm py-xs text-sm text-neutral-200 hover:bg-neutral-500 rounded-small cursor-pointer transition-colors duration-150 flex items-center gap-xs bg-transparent border-0"
            >
              <i className="fas fa-comment-alt text-xs" />
              Send Feedback
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-sm py-xs text-sm text-neutral-200 hover:bg-neutral-500 rounded-small cursor-pointer transition-colors duration-150 flex items-center gap-xs bg-transparent border-0"
            >
              <i className="fas fa-sign-out-alt text-xs" />
              Sign Out
            </button>
          </div>
        </div>
      )}
      <ProductFeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
      {showAdminModels && (
        <div className="fixed inset-0 bg-neutral-900/70 flex items-center justify-center z-[5000]">
          <div className="bg-neutral-700 border border-neutral-400 rounded-medium shadow-lg w-[min(560px,calc(100vw-32px))] max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-md py-sm border-b border-neutral-400">
              <h2 className="text-md font-semibold text-neutral-100 m-0">Admin Models</h2>
              <button
                className="bg-transparent border-0 text-neutral-200 hover:text-red-400 cursor-pointer"
                onClick={() => setShowAdminModels(false)}
                aria-label="Close admin models"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="p-md overflow-y-auto max-h-[calc(80vh-56px)]">
              {adminError && <div className="text-sm text-red-300 mb-sm">{adminError}</div>}
              {adminLoading && <div className="text-sm text-neutral-300 mb-sm">Loading...</div>}
              <div className="space-y-2">
                {models.map((model) => (
                  <div key={model.id} className="flex items-center justify-between gap-sm bg-neutral-600 border border-neutral-500 rounded-small px-sm py-sm">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-neutral-100 truncate">{model.id}</div>
                      <div className="text-xs text-neutral-300 truncate">{model.owned_by}</div>
                    </div>
                    <button
                      className="bg-red-700 hover:bg-red-600 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                      onClick={() => deleteModel(model.id)}
                      disabled={adminLoading}
                    >
                      Delete
                    </button>
                  </div>
                ))}
                {!adminLoading && models.length === 0 && (
                  <div className="text-sm text-neutral-300">No models found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showAdminUsers && (
        <div className="fixed inset-0 bg-neutral-900/70 flex items-center justify-center z-[5000]">
          <div className="bg-neutral-700 border border-neutral-400 rounded-medium shadow-lg w-[min(860px,calc(100vw-32px))] max-h-[86vh] overflow-hidden">
            <div className="flex items-center justify-between px-md py-sm border-b border-neutral-400">
              <h2 className="text-md font-semibold text-neutral-100 m-0">Admin Users</h2>
              <button
                className="bg-transparent border-0 text-neutral-200 hover:text-red-400 cursor-pointer"
                onClick={() => setShowAdminUsers(false)}
                aria-label="Close admin users"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="p-md overflow-y-auto max-h-[calc(86vh-56px)]">
              {adminError && <div className="text-sm text-red-300 mb-sm">{adminError}</div>}
              {adminLoading && <div className="text-sm text-neutral-300 mb-sm">Loading...</div>}

              <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto_auto_auto] gap-sm items-center bg-neutral-600 border border-neutral-500 rounded-small px-sm py-sm mb-md">
                <input
                  className="bg-neutral-800 border border-neutral-500 rounded-small px-sm py-xs text-neutral-100 text-sm"
                  placeholder="new.user@example.mil"
                  value={newUserEmail}
                  onChange={(event) => setNewUserEmail(event.target.value)}
                  disabled={adminLoading}
                />
                <input
                  className="bg-neutral-800 border border-neutral-500 rounded-small px-sm py-xs text-neutral-100 text-sm"
                  placeholder="Temporary password"
                  type="password"
                  value={newUserPassword}
                  onChange={(event) => setNewUserPassword(event.target.value)}
                  disabled={adminLoading}
                />
                <label htmlFor="new-user-admin" className="flex items-center gap-xs text-sm text-neutral-100">
                  <input
                    id="new-user-admin"
                    type="checkbox"
                    checked={newUserAdmin}
                    onChange={(event) => setNewUserAdmin(event.target.checked)}
                    disabled={adminLoading}
                  />
                  Admin
                </label>
                <label htmlFor="new-user-pii-phi" className="flex items-center gap-xs text-sm text-neutral-100">
                  <input
                    id="new-user-pii-phi"
                    type="checkbox"
                    checked={newUserPiiPhiEligible}
                    onChange={(event) => setNewUserPiiPhiEligible(event.target.checked)}
                    disabled={adminLoading}
                  />
                  PII/PHI eligible
                </label>
                <button
                  className="bg-red-700 hover:bg-red-600 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                  onClick={createUser}
                  disabled={adminLoading}
                >
                  Create
                </button>
              </div>

              <div className="space-y-2">
                {users.map((target) => {
                  const counts = target.resource_counts;
                  const isSelf = target.id === user.id;
                  return (
                    <div key={target.id} className="bg-neutral-600 border border-neutral-500 rounded-small px-sm py-sm">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-sm">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-neutral-100 truncate">{target.email}</div>
                          <div className="text-xs text-neutral-300 truncate">
                            {target.is_active ? 'Active' : 'Disabled'} · {target.is_superuser ? 'Admin' : 'User'} · {target.pii_phi_eligible ? 'PII/PHI eligible' : 'Masked only'} ·
                            {' '}{counts.chat_histories} chats · {counts.document_files} docs · {counts.collections} collections
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-xs">
                          <button
                            className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                            onClick={() => updateUser(target, { pii_phi_eligible: !target.pii_phi_eligible })}
                            disabled={adminLoading}
                          >
                            {target.pii_phi_eligible ? 'Revoke PII/PHI' : 'Grant PII/PHI'}
                          </button>
                          <button
                            className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                            onClick={() => updateUser(target, { is_active: !target.is_active })}
                            disabled={adminLoading || isSelf}
                          >
                            {target.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                            onClick={() => updateUser(target, { is_superuser: !target.is_superuser })}
                            disabled={adminLoading || isSelf}
                          >
                            {target.is_superuser ? 'Remove Admin' : 'Make Admin'}
                          </button>
                          <button
                            className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                            onClick={() => resetPassword(target)}
                            disabled={adminLoading}
                          >
                            Reset Password
                          </button>
                          <button
                            className="bg-red-700 hover:bg-red-600 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                            onClick={() => deleteUser(target)}
                            disabled={adminLoading || isSelf}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!adminLoading && users.length === 0 && (
                  <div className="text-sm text-neutral-300">No users found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showAdminAPIKeys && (
        <div className="fixed inset-0 bg-neutral-900/70 flex items-center justify-center z-[5000]">
          <div className="bg-neutral-700 border border-neutral-400 rounded-medium shadow-lg w-[min(760px,calc(100vw-32px))] max-h-[86vh] overflow-hidden">
            <div className="flex items-center justify-between px-md py-sm border-b border-neutral-400">
              <h2 className="text-md font-semibold text-neutral-100 m-0">Admin API Keys</h2>
              <button
                className="bg-transparent border-0 text-neutral-200 hover:text-red-400 cursor-pointer"
                onClick={() => {
                  setShowAdminAPIKeys(false);
                  setCreatedAPIKey(null);
                }}
                aria-label="Close admin API keys"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="p-md overflow-y-auto max-h-[calc(86vh-56px)]">
              {adminError && <div className="text-sm text-red-300 mb-sm">{adminError}</div>}
              {adminLoading && <div className="text-sm text-neutral-300 mb-sm">Loading...</div>}

              {createdAPIKey && (
                <div className="bg-amber-950/40 border border-amber-500 rounded-small p-sm mb-md">
                  <div className="text-sm font-semibold text-amber-200 mb-xs">
                    Copy this key now. It will not be shown again.
                  </div>
                  <div className="text-xs text-amber-100 mb-xs">
                    Register the same key on the Azure gateway before using it through the public API.
                  </div>
                  <div className="flex flex-col md:flex-row gap-xs">
                    <input
                      className="flex-1 min-w-0 bg-neutral-900 border border-neutral-500 rounded-small px-sm py-xs text-neutral-100 text-xs font-mono"
                      value={createdAPIKey}
                      readOnly
                      onFocus={(event) => event.currentTarget.select()}
                      aria-label="New API key"
                    />
                    <button
                      className="bg-red-700 hover:bg-red-600 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer"
                      onClick={copyCreatedAPIKey}
                    >
                      {apiKeyCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-sm items-center bg-neutral-600 border border-neutral-500 rounded-small px-sm py-sm mb-md">
                <input
                  className="bg-neutral-800 border border-neutral-500 rounded-small px-sm py-xs text-neutral-100 text-sm"
                  placeholder="Key name, e.g. Azure client"
                  value={newAPIKeyName}
                  onChange={(event) => setNewAPIKeyName(event.target.value)}
                  disabled={adminLoading}
                />
                <select
                  className="bg-neutral-800 border border-neutral-500 rounded-small px-sm py-xs text-neutral-100 text-sm"
                  value={newAPIKeyUserId}
                  onChange={(event) => setNewAPIKeyUserId(event.target.value)}
                  disabled={adminLoading}
                  aria-label="API key user"
                >
                  <option value="" disabled>Select user</option>
                  {users.filter((target) => target.is_active).map((target) => (
                    <option key={target.id} value={target.id}>{target.email}</option>
                  ))}
                </select>
                <button
                  className="bg-red-700 hover:bg-red-600 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                  onClick={createAPIKey}
                  disabled={adminLoading || !newAPIKeyName.trim() || !newAPIKeyUserId}
                >
                  Create Key
                </button>
              </div>

              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-sm bg-neutral-600 border border-neutral-500 rounded-small px-sm py-sm">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-neutral-100 truncate">{key.name}</div>
                      <div className="text-xs text-neutral-300 truncate">
                        {key.user_email} · {key.key_prefix}… · {key.is_active ? 'Active' : 'Revoked'}
                      </div>
                      <div className="text-xs text-neutral-400 truncate">
                        Created {new Date(key.created_at).toLocaleString()}
                        {key.last_used_at ? ` · Last used ${new Date(key.last_used_at).toLocaleString()}` : ' · Never used'}
                      </div>
                    </div>
                    <button
                      className="bg-red-700 hover:bg-red-600 text-neutral-100 px-sm py-xs rounded-small border-0 cursor-pointer disabled:opacity-50"
                      onClick={() => revokeAPIKey(key)}
                      disabled={adminLoading || !key.is_active}
                    >
                      {key.is_active ? 'Revoke' : 'Revoked'}
                    </button>
                  </div>
                ))}
                {!adminLoading && apiKeys.length === 0 && (
                  <div className="text-sm text-neutral-300">No API keys found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
